import { useEffect, useMemo, useState } from 'react';
import { useLeads } from '@/leads/LeadsProvider';
import { ouvirProximosContatos } from '@/services/tarefasService';
import { leadsComTarefaAberta } from '@/leads/semProximoContato';

const ASSUNTO = 'os próximos contatos';

/**
 * ★ PC-4 — as tarefas ABERTAS da clínica, para o filtro "Sem próximo contato".
 *
 * ★ Assinada SÓ no Funil e na Lista, não no `LeadsProvider` (decisão do dono, 18/out): no
 * provider, Dashboard, Recebimentos e as outras telas pagariam os ~465 documentos de Campinas
 * sem usar. É a MESMA consulta da tela Próximos contatos (`ouvirProximosContatos(clinicaId, {})`,
 * índice que já existe), pelo caminho vigiado da WU-2.
 *
 * ⚠⚠ `comAberta` é `null` enquanto a lista NÃO é confiável: antes da 1ª resposta do servidor,
 * ou com a assinatura em falha. Com `null` o filtro não é aplicado e a barra avisa — ★ um "sem
 * próximo contato" calculado sem as tarefas mostraria todo mundo das 3 etapas.
 *
 * ★ Depois de confirmada, uma emissão do cache (a conexão caiu) NÃO desliga o filtro: aí é o
 * caso "pode estar desatualizado" da WU-2, e a faixa única do provider avisa
 * (`definirDesatualizado`, assunto 'os próximos contatos').
 *
 * @returns {{ comAberta: Set<string>|null, estado: 'conferindo'|'ok'|'falhou' }}
 */
export function useTarefasAbertas(clinicaId) {
  const { definirDesatualizado } = useLeads();
  const [lista, setLista] = useState([]);
  const [confirmado, setConfirmado] = useState(false);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    setLista([]);
    setConfirmado(false);
    setFalhou(false);
    if (!clinicaId) return undefined;
    const cancelar = ouvirProximosContatos(
      clinicaId,
      {},
      (tarefas, meta) => {
        setLista(tarefas ?? []);
        // ⚠ Só o servidor confirma; sem a marca (dublê, caminho antigo) segue "conferindo".
        if (meta?.doServidor === true) {
          setConfirmado(true);
          setFalhou(false);
        }
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir as tarefas abertas:', e);
        setFalhou(true);
      },
      (velho) => definirDesatualizado?.(ASSUNTO, velho),
    );
    return () => {
      cancelar?.();
      // ★ A faixa não pode ficar acesa depois que a tela que a acendeu saiu.
      definirDesatualizado?.(ASSUNTO, false);
    };
  }, [clinicaId, definirDesatualizado]);

  const confiavel = confirmado && !falhou;
  const comAberta = useMemo(() => (confiavel ? leadsComTarefaAberta(lista) : null), [confiavel, lista]);
  return { comAberta, estado: falhou ? 'falhou' : confiavel ? 'ok' : 'conferindo' };
}
